import React, {useState} from 'react';
import {Box, Button, Card, CardContent, Chip, Grid, IconButton, Typography} from '@mui/material';
import {Add, Delete, Edit} from '@mui/icons-material';
import dayjs from 'dayjs';
import {IEtkinlik, IKalem} from '../../../constants/types/logbook';
import EtkinlikFormDialog from './EtkinlikFormDialog';
import logbookApi from "../../../api/staffApi/logbookApi";

interface EtkinliklerProps {
    etkinlikler: IKalem<IEtkinlik>;
    setEtkinlikler: (etkinlikler: IKalem<IEtkinlik>) => void;
    canEdit?: boolean;
    seciliTarih: dayjs.Dayjs | null;
    takvimVerileriGetir: () => void;
}

const Etkinlikler = ({etkinlikler, canEdit = true, seciliTarih, takvimVerileriGetir}: EtkinliklerProps) => {
    const [formOpen, setFormOpen] = useState(false);
    const [editingEtkinlik, setEditingEtkinlik] = useState<{
        index: number,
        type: 'existing' | 'new',
        data: IEtkinlik
    } | null>(null);

    const handleAddClick = () => {
        setEditingEtkinlik(null);
        setFormOpen(true);
    };

    const handleEditClick = (index: number, type: 'existing' | 'new', data: IEtkinlik) => {
        setEditingEtkinlik({index, type, data});
        setFormOpen(true);
    };

    const handleSaveForm = (data: IEtkinlik, file?: File) => {
        const dateStr = seciliTarih?.format('DD-MM-YYYY') ?? '';
        const dataWithDate = {...data, etkinlik_tarihi: dateStr};

        if (file) {
            // Görsel varsa FormData ile gönder
            const formData = new FormData();
            formData.append('tarih', dateStr);
            formData.append('file', file);

            // Etkinlik verilerini JSON string olarak ekle
            const etkinlikData = {
                eklenenListe: editingEtkinlik?.type === 'new' || !editingEtkinlik ? [dataWithDate] : [],
                guncellenecekListe: editingEtkinlik?.type === 'existing' ? {[dataWithDate.id!]: dataWithDate} : {},
                silinecekIdListe: []
            };
            formData.append('etkinlikler', JSON.stringify(etkinlikData));

            logbookApi.postEtkinlikler(formData as any).then(() => {
                takvimVerileriGetir();
            });
        } else {
            // Görsel yoksa normal JSON gönder
            const payload = {
                tarih: dateStr,
                etkinlikler: {
                    eklenenListe: editingEtkinlik?.type === 'new' || !editingEtkinlik ? [dataWithDate] : [],
                    guncellenecekListe: editingEtkinlik?.type === 'existing' ? {[dataWithDate.id!]: dataWithDate} : {},
                    silinecekIdListe: []
                }
            };

            logbookApi.postEtkinlikler(payload).then(() => {
                takvimVerileriGetir();
            });
        }
    };

    const handleDelete = (id: number) => {
        const payload = {
            tarih: seciliTarih?.format('DD-MM-YYYY') ?? '',
            etkinlikler: {
                eklenenListe: [],
                guncellenecekListe: {},
                silinecekIdListe: [id]
            }
        };

        logbookApi.postEtkinlikler(payload).then(() => {
            takvimVerileriGetir();
        });
    };

    return (
        <Box>
            <Grid container spacing={2}>
                {etkinlikler.data.map((etkinlik, index) => {
                    return (
                        <Grid item xs={12} key={`existing-${etkinlik.id}`}>
                            <Card>
                                <CardContent sx={{py: 1, '&:last-child': {pb: 1}}}>
                                    <Grid container alignItems="center">
                                        <Grid item xs={2}>
                                            {etkinlik.etkinlik_gorsel ? (
                                                <Box
                                                    component="img"
                                                    src={etkinlik.etkinlik_gorsel}
                                                    sx={{
                                                        width: '100%',
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 1
                                                    }}
                                                />
                                            ) : (
                                                <Box sx={{
                                                    width: '100%',
                                                    height: 60,
                                                    bgcolor: '#f0f0f0',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Typography variant="caption">Görsel Yok</Typography>
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={6} sx={{pl: 2}}>
                                            <Typography variant="h6">{etkinlik.isim}</Typography>
                                            <Typography variant="body2" color="textSecondary" noWrap>
                                                {etkinlik.aciklama}
                                            </Typography>
                                            <Box sx={{mt: 0.5}}>
                                                <Chip size="small"
                                                      label={`${etkinlik.etkinlik_baslangic_saati.slice(0, 5)} - ${etkinlik.etkinlik_bitis_saati.slice(0, 5)}`}
                                                      sx={{mr: 1}}/>
                                                <Chip size="small" color="primary" variant="outlined"
                                                      label={`${etkinlik.katilim_ucreti} TL`}/>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4} sx={{display: 'flex', justifyContent: 'end'}}>
                                            <IconButton disabled={!canEdit}
                                                        onClick={() => handleEditClick(index, 'existing', etkinlik)}
                                                        color="primary">
                                                <Edit/>
                                            </IconButton>
                                            <IconButton disabled={!canEdit} onClick={() => handleDelete(etkinlik.id!)}
                                                        color="error">
                                                <Delete/>
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}

                <Grid item xs={12} sx={{display: 'flex', justifyContent: 'end', mt: 1}}>
                    <Button
                        variant="contained"
                        startIcon={<Add/>}
                        onClick={handleAddClick}
                        disabled={!canEdit}
                    >
                        Yeni Etkinlik Ekle
                    </Button>
                </Grid>
            </Grid>

            <EtkinlikFormDialog
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSave={handleSaveForm}
                initialData={editingEtkinlik?.data}
            />
        </Box>
    );
};

export default Etkinlikler;
